import React, { useEffect, useMemo, useState } from "react";
import DropdownSchoolYear from "../../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownTermDate from "../../../../../unicomponents/Dropdowns/DropdownTermDate";
import DropdownHierarchyOne from "../../../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import styles from "../../css/ParentEducation.module.css";
import HelpHyperLink from "../../../../../unicomponents/HelpHyperLink";

const toCourseValue = (value) => String(value ?? "");

const isIntegratedValue = (value) =>
  String(value ?? "") === "0" || String(value ?? "") === "";

const scopeFilter = (period, schoolYearId, termId, courseValue) =>
  String(period.school_year_id) === String(schoolYearId) &&
  String(period.term_id) === String(termId) &&
  (isIntegratedValue(courseValue)
    ? isIntegratedValue(period.hierarchy_one)
    : String(period.hierarchy_one ?? "") === String(courseValue ?? ""));

const makeTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export default function ParentEducationSettingsPage({
  schoolId,
  periods,
  setPeriods,
  settingData,
  termPeriodData,
  canRead,
  canCreate,
  canUpdate,
  canDelete,
  handleToastCenterTop,
  setToastConfirmModalState,
  createPeriodMutation,
  updatePeriodMutation,
  deletePeriodMutation,
  loadPreviousPeriodsMutation,
}) {
  const [scope, setScope] = useState({
    schoolYearId: "",
    schoolYear: null,
    termId: "",
    hierarchyOneId: "",
  });
  const [selectedId, setSelectedId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState([]);
  const [prevScope, setPrevScope] = useState({
    fromSchoolYearId: "",
    fromSchoolYear: null,
    fromTermId: "",
    fromHierarchyOneId: "",
  });

  const filteredPeriods = useMemo(() => {
    if (!scope.schoolYearId || !scope.termId || !scope.hierarchyOneId)
      return [];
    const courseValue = toCourseValue(scope.hierarchyOneId);
    return (periods || [])
      .filter((row) =>
        scopeFilter(row, scope.schoolYearId, scope.termId, courseValue),
      )
      .sort(
        (a, b) => Number(a.display_order || 0) - Number(b.display_order || 0),
      );
  }, [periods, scope.schoolYearId, scope.termId, scope.hierarchyOneId]);

  const canSeeSettings = canRead || canCreate || canUpdate || canDelete;

  useEffect(() => {
    if (scope.schoolYear !== null || !settingData?.length) return;
    const sorted = settingData
      .filter((x) => x.hierarchy_level === 0 && x.parent_id === 0)
      .sort((a, b) => Number(b.school_year) - Number(a.school_year));
    if (!sorted.length) return;
    const initial = sorted[0];
    setScope((prev) => ({
      ...prev,
      schoolYearId: String(initial.id),
      schoolYear: initial.school_year,
    }));
    setPrevScope((prev) => ({
      ...prev,
      fromSchoolYearId: String(initial.id),
      fromSchoolYear: initial.school_year,
    }));
  }, [settingData, scope.schoolYear]);

  const handleScopeChange = (e) => {
    const { name, value } = e.target;
    const selectedText = e.target.selectedOptions?.[0]?.text || "";
    if (name === "0") {
      setScope({
        schoolYearId: value,
        schoolYear: selectedText,
        termId: "",
        hierarchyOneId: "",
      });
      setSelectedId("");
      return;
    }
    if (name === "termDate") {
      setScope((prev) => ({ ...prev, termId: value }));
      setSelectedId("");
      return;
    }
    if (name === "1") {
      setScope((prev) => ({ ...prev, hierarchyOneId: value }));
      setSelectedId("");
    }
  };

  const handlePrevScopeChange = (e) => {
    const { name, value } = e.target;
    const selectedText = e.target.selectedOptions?.[0]?.text || "";
    if (name === "0") {
      setPrevScope({
        fromSchoolYearId: value,
        fromSchoolYear: selectedText,
        fromTermId: "",
        fromHierarchyOneId: "",
      });
      return;
    }
    if (name === "termDate") {
      setPrevScope((prev) => ({ ...prev, fromTermId: value }));
      return;
    }
    if (name === "1") {
      setPrevScope((prev) => ({ ...prev, fromHierarchyOneId: value }));
    }
  };

  const handleFieldChange = (id, field, value) => {
    setPeriods((prev) =>
      prev.map((row) =>
        String(row.id) === String(id) ? { ...row, [field]: value } : row,
      ),
    );
  };

  const validateRow = (row) => {
    if (!row.session_name?.trim()) {
      handleToastCenterTop("기간명을 입력해 주세요.");
      return false;
    }
    return true;
  };

  const upsertOneRow = async (row) => {
    const body = {
      schoolid: schoolId,
      school_year_id: scope.schoolYearId,
      term_id: scope.termId,
      hierarchy_one: toCourseValue(scope.hierarchyOneId),
      session_name: row.session_name.trim(),
      education_content: row.education_content || "",
      event_date: row.event_date || "",
      note: row.note || "",
      display_order: Number(row.display_order || 0),
    };
    const isTemp = String(row.id).startsWith("temp_");
    if (isTemp) {
      const created = await createPeriodMutation.mutateAsync(body);
      if (created?.id) {
        setPeriods((prev) =>
          prev.map((x) =>
            String(x.id) === String(row.id)
              ? {
                  ...x,
                  id: created.id,
                }
              : x,
          ),
        );
      }
      return;
    }
    await updatePeriodMutation.mutateAsync({ id: row.id, body });
  };

  const handleAddRow = () => {
    if (!canCreate) {
      handleToastCenterTop("생성 권한이 없습니다.");
      return;
    }
    if (!scope.schoolYearId || !scope.termId || !scope.hierarchyOneId) {
      handleToastCenterTop("학년도/학기/과정을 먼저 선택해 주세요.");
      return;
    }
    const nextOrder =
      (filteredPeriods[filteredPeriods.length - 1]?.display_order || 0) + 1;
    const newRow = {
      id: makeTempId(),
      school_year_id: scope.schoolYearId,
      term_id: scope.termId,
      hierarchy_one: toCourseValue(scope.hierarchyOneId),
      session_name: "",
      education_content: "",
      event_date: "",
      note: "",
      display_order: nextOrder,
    };
    setPeriods((prev) => [...prev, newRow]);
    setSelectedId(newRow.id);
  };

  const handleSaveRow = (row) => {
    const isTemp = String(row.id).startsWith("temp_");
    if (isTemp && !canCreate) {
      handleToastCenterTop("생성 권한이 없습니다.");
      return;
    }
    if (!isTemp && !canUpdate) {
      handleToastCenterTop("수정 권한이 없습니다.");
      return;
    }
    if (!validateRow(row)) {
      return;
    }
    upsertOneRow(row)
      .then(() => handleToastCenterTop("저장 완료"))
      .catch(() => handleToastCenterTop("저장 실패"));
  };

  const handleSaveAll = async () => {
    const hasCreateTarget = filteredPeriods.some((row) =>
      String(row.id).startsWith("temp_"),
    );
    if (hasCreateTarget && !canCreate) {
      handleToastCenterTop("생성 권한이 없습니다.");
      return;
    }
    const hasUpdateTarget = filteredPeriods.some(
      (row) => !String(row.id).startsWith("temp_"),
    );
    if (hasUpdateTarget && !canUpdate) {
      handleToastCenterTop("수정 권한이 없습니다.");
      return;
    }
    const invalid = filteredPeriods.find((row) => !row.session_name?.trim());
    if (invalid) {
      handleToastCenterTop("기간명이 비어 있는 행이 있습니다.");
      return;
    }
    try {
      for (const row of filteredPeriods) {
        await upsertOneRow(row);
      }
      handleToastCenterTop("모두 저장 완료");
    } catch (error) {
      handleToastCenterTop("모두 저장 실패");
    }
  };

  const handleDeleteRow = (row) => {
    if (!canDelete) {
      handleToastCenterTop("삭제 권한이 없습니다.");
      return;
    }
    if (String(row.id).startsWith("temp_")) {
      setPeriods((prev) => prev.filter((x) => String(x.id) !== String(row.id)));
      setConfirmDelete([]);
      return;
    }
    setToastConfirmModalState({
      visible: true,
      message:
        "해당 기간을 기준으로 기록된 출석 정보 역시 모두 삭제 됩니다. 삭제하시겠습니까?",
      onConfirm: () => {
        deletePeriodMutation.mutate(row.id, {
          onSuccess: () => {
            handleToastCenterTop("삭제 완료");
            setConfirmDelete([]);
          },
          onError: () => handleToastCenterTop("삭제 실패"),
        });
        setToastConfirmModalState((prev) => ({ ...prev, visible: false }));
      },
      onCancel: () =>
        setToastConfirmModalState((prev) => ({ ...prev, visible: false })),
    });
  };

  const handleConfirmDelete = (id) => {
    setConfirmDelete((prev) => [...prev, id]);
  };

  const handleCancelDelete = () => {
    setConfirmDelete([]);
  };

  const handleLoadPrevious = () => {
    if (!canCreate && !canUpdate) {
      handleToastCenterTop("불러오기 권한이 없습니다.");
      return;
    }
    if (
      !scope.schoolYearId ||
      !scope.termId ||
      !scope.hierarchyOneId ||
      !prevScope.fromSchoolYearId ||
      !prevScope.fromTermId ||
      !prevScope.fromHierarchyOneId
    ) {
      handleToastCenterTop(
        "현재/이전 기준(학년도-학기-과정)을 모두 선택해 주세요.",
      );
      return;
    }
    loadPreviousPeriodsMutation.mutate(
      {
        schoolid: schoolId,
        fromSchoolYearId: prevScope.fromSchoolYearId,
        fromTermId: prevScope.fromTermId,
        fromHierarchyOne: prevScope.fromHierarchyOneId,
      },
      {
        onSuccess: (rows) => {
          const existingNames = new Set(
            filteredPeriods
              .map((row) => row.session_name?.trim())
              .filter(Boolean),
          );
          const copied = (rows || [])
            .filter((row) => !existingNames.has(row.session_name?.trim()))
            .map((row, index) => ({
              id: makeTempId(),
              school_year_id: scope.schoolYearId,
              term_id: scope.termId,
              hierarchy_one: toCourseValue(scope.hierarchyOneId),
              session_name: row.session_name || "",
              education_content: row.education_content || "",
              event_date: row.event_date || "",
              note: row.note || "",
              display_order:
                Number(
                  filteredPeriods[filteredPeriods.length - 1]?.display_order ||
                    0,
                ) +
                index +
                1,
            }));
          if (copied.length === 0) {
            handleToastCenterTop(
              "불러올 데이터가 없거나 이미 모두 존재합니다.",
            );
            return;
          }
          setPeriods((prev) => [...prev, ...copied]);
          handleToastCenterTop(
            `${copied.length}개 기간을 불러왔습니다. 저장 버튼으로 확정해 주세요.`,
          );
        },
        onError: () => handleToastCenterTop("이전 기간 불러오기 실패"),
      },
    );
  };

  if (!canSeeSettings) {
    return (
      <div className="alert alert-warning">설정 메뉴 접근 권한이 없습니다.</div>
    );
  }

  return (
    <div className="mt-2">
      <div className={`${styles.filterRow} mb-2`}>
        <div style={{ width: 100 }}>
          <DropdownSchoolYear
            settingData={settingData}
            handleDropdownChange={handleScopeChange}
            schoolYearId={scope.schoolYearId}
            allYears={false}
          />
        </div>
        <div style={{ width: 110 }}>
          <DropdownTermDate
            termPeriodData={termPeriodData}
            handleDropdownChange={handleScopeChange}
            termId={scope.termId}
            schoolYearId={scope.schoolYearId}
            hierarchyOneId={scope.hierarchyOneId}
            hierarchyTwoId={"0"}
          />
        </div>
        <div style={{ width: 120 }}>
          <DropdownHierarchyOne
            settingData={settingData}
            handleDropdownChange={handleScopeChange}
            schoolYear={scope.schoolYear}
            hierarchyOneId={scope.hierarchyOneId}
            hierarchyOneTitle={"통합"}
            extraOption={true}
            extraOptionTitle={"과정"}
          />
        </div>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={handleAddRow}
        >
          + 기간 추가
        </button>
        <HelpHyperLink link={"https://youtu.be/miYu1-2cQ0g"} />
      </div>

      <div className="border rounded p-2 mb-3">
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="fw-bold mb-0">
            이전 기간 불러오기 (학년도-학기-과정 기준)
          </div>
          <div
            className={`${styles.filterRow} mb-0`}
            style={{ justifyContent: "flex-end" }}
          >
            <div style={{ width: 100 }}>
              <DropdownSchoolYear
                settingData={settingData}
                handleDropdownChange={handlePrevScopeChange}
                schoolYearId={prevScope.fromSchoolYearId}
                allYears={false}
              />
            </div>
            <div style={{ width: 110 }}>
              <DropdownTermDate
                termPeriodData={termPeriodData}
                handleDropdownChange={handlePrevScopeChange}
                termId={prevScope.fromTermId}
                schoolYearId={prevScope.fromSchoolYearId}
                hierarchyOneId={prevScope.fromHierarchyOneId}
                hierarchyTwoId={"0"}
              />
            </div>
            <div style={{ width: 120 }}>
              <DropdownHierarchyOne
                settingData={settingData}
                handleDropdownChange={handlePrevScopeChange}
                schoolYear={prevScope.fromSchoolYear}
                hierarchyOneId={prevScope.fromHierarchyOneId}
                hierarchyOneTitle={"통합"}
                extraOption={true}
                extraOptionTitle={"과정"}
              />
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={handleLoadPrevious}
            >
              이전 기간 불러오기
            </button>
          </div>
        </div>
      </div>

      <table className="table table-bordered table-sm">
        <thead className="table-light">
          <tr>
            <th style={{ width: 70 }}>순서</th>
            <th style={{ width: 180 }}>
              기간<span style={{ color: "red", fontSize: "12px" }}>(필수)</span>
            </th>
            <th>교육 내용</th>
            <th style={{ width: 130 }}>날짜</th>
            <th style={{ width: 180 }}>비고</th>
            <th style={{ width: 180 }}>
              <div className="d-flex align-items-center justify-content-between gap-1">
                <span>저장/삭제</span>
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  onClick={handleSaveAll}
                  disabled={
                    createPeriodMutation.isLoading ||
                    updatePeriodMutation.isLoading
                  }
                >
                  모두저장
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredPeriods.map((row) => {
            const isTemp = String(row.id).startsWith("temp_");
            const canEdit = isTemp ? canCreate : canUpdate;
            return (
              <tr
                key={row.id}
                className={
                  String(selectedId) === String(row.id)
                    ? styles.settingRowSelected
                    : ""
                }
                onClick={() => setSelectedId(row.id)}
              >
                <td>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    value={row.display_order || 0}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFieldChange(row.id, "display_order", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    value={row.session_name || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFieldChange(row.id, "session_name", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    value={row.education_content || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFieldChange(
                        row.id,
                        "education_content",
                        e.target.value,
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    value={row.event_date || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFieldChange(row.id, "event_date", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    value={row.note || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFieldChange(row.id, "note", e.target.value)
                    }
                  />
                </td>
                <td>
                  <div className="d-flex gap-0">
                    <button
                      className="btn btn-sm btn-primary"
                      type="button"
                      onClick={() => handleSaveRow(row)}
                    >
                      저장
                    </button>
                    {confirmDelete.includes(row.id) ? (
                      <>
                        <button
                          className="btn btn-sm btn-danger"
                          type="button"
                          onClick={() => handleDeleteRow(row)}
                          disabled={!canDelete}
                        >
                          확인
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          type="button"
                          onClick={handleCancelDelete}
                        >
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm btn-warning"
                        type="button"
                        onClick={() => handleConfirmDelete(row.id)}
                        disabled={!canDelete}
                      >
                        삭제
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {filteredPeriods.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-3">
                조건에 맞는 기간이 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
